import i18n from '@/assets/i18n/i18n'
import usageApi from '@/api/usageApi'
import { usageErrorText } from './usage'

// The review list's column values → the API's sort keys
const SORTS = {
    airing: 'airing',
    work: 'work',
    outcome: 'outcome',
    reason: 'reason',
    decision: 'reviewed'
}
// What the review opens on: the findings that need a person
const DEFAULT_OUTCOMES = ['complaint', 'needs_review']

const state = {
    usageReviewScheduleId: null,
    usageReviewSchedule: null,
    usageReviewComparison: null, // { comparisonId, comparedAt, counts }
    usageReviewNotReady: null,   // error of a schedule without a finished comparison

// List
    usageReview: [],
    selectedUsageReview: [],
    isAllSelectedUsageReview: false,
    expandedUsageReview: [],
    usageReviewDetails: [],
    usageReviewServerSideSortingOptions: { sortBy: ['airing'], sortDesc: [false] },

// Filters
    usageReviewSearch: '',
    selectedUsageReviewOutcomeFilter: DEFAULT_OUTCOMES,
    selectedUsageReviewDecisionFilter: [],
    selectedUsageReviewReasonFilter: [],
    selectedUsageReviewExportedFilter: null,

// Actions
    selectedUsageReviewAction: [],
    usageReviewBulkDialog: { show: false },
    showComplaintExportDialog: false,
    selectedComplaintExportDateFrom: null,
    selectedComplaintExportDateTo: null
}

const getters = {
    getUsageReviewScheduleId                    (state) { return state.usageReviewScheduleId },
    getUsageReviewSchedule                      (state) { return state.usageReviewSchedule },
    getUsageReviewComparison                    (state) { return state.usageReviewComparison },
    getUsageReviewNotReady                      (state) { return state.usageReviewNotReady },
    getUsageReview                              (state) { return state.usageReview },
    getSelectedUsageReview                      (state) { return state.selectedUsageReview },
    getIsAllSelectedUsageReview                 (state) { return state.isAllSelectedUsageReview },
    getExpandedUsageReview                      (state) { return state.expandedUsageReview },
    getUsageReviewDetails                       (state) { return state.usageReviewDetails },
    getUsageReviewServerSideSortingOptions      (state) { return state.usageReviewServerSideSortingOptions },
    getUsageReviewSearch                        (state) { return state.usageReviewSearch },
    getSelectedUsageReviewOutcomeFilter         (state) { return state.selectedUsageReviewOutcomeFilter },
    getSelectedUsageReviewDecisionFilter        (state) { return state.selectedUsageReviewDecisionFilter },
    getSelectedUsageReviewReasonFilter          (state) { return state.selectedUsageReviewReasonFilter },
    getSelectedUsageReviewExportedFilter        (state) { return state.selectedUsageReviewExportedFilter },
    getSelectedUsageReviewAction                (state) { return state.selectedUsageReviewAction },
    getUsageReviewBulkDialog                    (state) { return state.usageReviewBulkDialog },
    getShowComplaintExportDialog                (state) { return state.showComplaintExportDialog },
    getSelectedComplaintExportDateFrom          (state) { return state.selectedComplaintExportDateFrom },
    getSelectedComplaintExportDateTo            (state) { return state.selectedComplaintExportDateTo },
    // The list's filters as the API reads them; the bulk review sends the same
    getUsageReviewFilter(state) {
        return {
            outcome: state.selectedUsageReviewOutcomeFilter,
            decision: state.selectedUsageReviewDecisionFilter,
            reason: state.selectedUsageReviewReasonFilter,
            exported: state.selectedUsageReviewExportedFilter,
            search: state.usageReviewSearch
        }
    }
}

const mutations = {
    setUsageReviewScheduleId:                   (state, id) => { state.usageReviewScheduleId = id },
    setUsageReviewSchedule:                     (state, schedule) => { state.usageReviewSchedule = schedule },
    setUsageReviewComparison:                   (state, comparison) => { state.usageReviewComparison = comparison },
    setUsageReviewNotReady:                     (state, error) => { state.usageReviewNotReady = error },
    setUsageReview:                             (state, rows) => { state.usageReview = rows },
    setSelectedUsageReview:                     (state, ids) => { state.selectedUsageReview = ids },
    setIsAllSelectedUsageReview:                (state, value) => { state.isAllSelectedUsageReview = value },
    setExpandedUsageReview:                     (state, items) => { state.expandedUsageReview = items },
    setUsageReviewDetails:                      (state, details) => { state.usageReviewDetails = details },
    setUsageReviewServerSideSortingOptions:     (state, options) => { state.usageReviewServerSideSortingOptions = options },
    setUsageReviewSearch:                       (state, value) => { state.usageReviewSearch = value },
    setSelectedUsageReviewOutcomeFilter:        (state, value) => { state.selectedUsageReviewOutcomeFilter = value || [] },
    setSelectedUsageReviewDecisionFilter:       (state, value) => { state.selectedUsageReviewDecisionFilter = value || [] },
    setSelectedUsageReviewReasonFilter:         (state, value) => { state.selectedUsageReviewReasonFilter = value || [] },
    setSelectedUsageReviewExportedFilter:       (state, value) => { state.selectedUsageReviewExportedFilter = value },
    setSelectedUsageReviewAction:               (state, value) => { state.selectedUsageReviewAction = value },
    setUsageReviewBulkDialog:                   (state, dialog) => { state.usageReviewBulkDialog = dialog },
    setShowComplaintExportDialog:               (state, show) => { state.showComplaintExportDialog = show },
    setSelectedComplaintExportDateFrom:         (state, date) => { state.selectedComplaintExportDateFrom = date },
    setSelectedComplaintExportDateTo:           (state, date) => { state.selectedComplaintExportDateTo = date },
    // A reviewed finding, in the list and in its open row
    replaceUsageReviewFinding:                  (state, finding) => {
        const row = { ...finding, alvId: finding.id }
        state.usageReview = state.usageReview.map(f => f.id === row.id ? row : f)
        state.expandedUsageReview = state.expandedUsageReview.map(f => f.id === row.id ? row : f)
        state.usageReviewDetails = state.usageReviewDetails.map(f => f.id === row.id ? row : f)
    }
}

const actions = {
    // Opening the review of another schedule starts from the default filters
    async openUsageReview({ commit, dispatch, getters }, scheduleId) {
        if (getters.getUsageReviewScheduleId !== scheduleId) {
            commit('setUsageReviewScheduleId', scheduleId)
            commit('setUsageReview', [])
            commit('setSelectedUsageReview', [])
            commit('setIsAllSelectedUsageReview', false)
            commit('setExpandedUsageReview', [])
            commit('setUsageReviewSearch', '')
            commit('setSelectedUsageReviewOutcomeFilter', DEFAULT_OUTCOMES)
            commit('setSelectedUsageReviewDecisionFilter', [])
            commit('setSelectedUsageReviewReasonFilter', [])
            commit('setSelectedUsageReviewExportedFilter', null)
        }
        commit('setUsageReviewSchedule', null)
        const detail = await usageApi.detail(scheduleId)
        if (detail.ok) commit('setUsageReviewSchedule', detail.data)
        else commit('setSnackbar', { type: 'error', msg: usageErrorText(detail.error) })
    },

    async fetchUsageReview({ commit, getters }) {
        commit('setIsLoading', true)
        const sort = getters.getUsageReviewServerSideSortingOptions || {}
        const sortBy = sort.sortBy && sort.sortBy[0] ? SORTS[sort.sortBy[0]] : null
        const result = await usageApi.findings(getters.getUsageReviewScheduleId, {
            ...getters.getUsageReviewFilter,
            sort: sortBy,
            direction: sortBy ? (sort.sortDesc && sort.sortDesc[0] ? 'desc' : 'asc') : null,
            page: getters.getCurrentPage,
            perPage: getters.getItemsPerPage
        })
        commit('setIsLoading', false)
        if (!result.ok) {
            commit('setUsageReview', [])
            if (result.error.code === 'comparison_not_ready') return commit('setUsageReviewNotReady', result.error)
            commit('setNoDataText', 'lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')
            return commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
        }
        commit('setUsageReviewNotReady', null)
        commit('setUsageReviewComparison', { comparisonId: result.data.comparisonId, comparedAt: result.data.comparedAt, counts: result.data.counts })
        commit('setUsageReview', result.data.rows.map(row => ({ ...row, alvId: row.id })))
        if (result.data.rows.length === 0) commit('setNoDataText', 'lang_GENERAL_MESSAGE_NO_MATCHING_RECORDS_FOUND')
        this._vm.$alvPlugin.storePagingInformations(result.data.total, result.data.page, result.data.counts.total)
    },

    // A row opens with what the list already holds; nothing to fetch
    fetchUsageReviewDetails({ commit, getters }, id) {
        commit('setIsLoading', false)
        const row = getters.getUsageReview.find(f => f.id === id)
        commit('setUsageReviewDetails', row ? [row] : [])
    },

    async reviewUsageFinding({ commit, dispatch, getters }, { findingId, body }) {
        const result = await usageApi.reviewFinding(getters.getUsageReviewScheduleId, findingId, body)
        if (!result.ok) {
            commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
            return false
        }
        commit('replaceUsageReviewFinding', result.data)
        dispatch('refreshUsageReviewCounts')
        return true
    },

    // The counts move with every decision; the rows stay as they are until the next search
    async refreshUsageReviewCounts({ commit, getters }) {
        const result = await usageApi.findings(getters.getUsageReviewScheduleId, { perPage: 1 })
        if (result.ok) commit('setUsageReviewComparison', { comparisonId: result.data.comparisonId, comparedAt: result.data.comparedAt, counts: result.data.counts })
    },

// ACTIONS dropdown
    doUsageReviewAction({ commit, getters }) {
        const action = getters.getSelectedUsageReviewAction
        const selected = getters.getSelectedUsageReview
        const needs = (text) => {
            commit('setSelectionRequiredText', text)
            commit('setShowSelectionRequiredDialog', true)
        }
        switch (action) {
            case 'acceptSelected':
            case 'rejectSelected':
            case 'undecideSelected':
                if (selected.length === 0) return needs('lang_GENERAL_SELECTION_REQUIRED_DIALOG_TEXT_AT_LEAST_ONE')
                return commit('setUsageReviewBulkDialog', { show: true, scope: 'selected', decision: { acceptSelected: 'accepted', rejectSelected: 'rejected', undecideSelected: 'undecided' }[action], count: selected.length })
            case 'acceptFiltered':
            case 'rejectFiltered':
                return commit('setUsageReviewBulkDialog', { show: true, scope: 'filter', decision: action === 'acceptFiltered' ? 'accepted' : 'rejected', count: getters.getTotalListed })
            case 'export':
                return commit('setShowComplaintExportDialog', true)
        }
    },

    async runUsageReviewBulk({ commit, dispatch, getters }) {
        const dialog = getters.getUsageReviewBulkDialog
        commit('setUsageReviewBulkDialog', { ...dialog, running: true })
        const body = dialog.scope === 'selected'
            ? { decision: dialog.decision, findingIds: getters.getSelectedUsageReview }
            : { decision: dialog.decision, filter: getters.getUsageReviewFilter }
        const result = await usageApi.reviewFindings(getters.getUsageReviewScheduleId, body)
        if (!result.ok) {
            commit('setUsageReviewBulkDialog', { ...dialog, running: false, error: usageErrorText(result.error) })
            return
        }
        commit('setUsageReviewBulkDialog', { show: false })
        commit('setSelectedUsageReview', [])
        commit('setIsAllSelectedUsageReview', false)
        commit('setSelectedUsageReviewAction', [])
        commit('setSnackbar', {
            type: 'success',
            msg: i18n.t('lang_PUBLISHING_USAGE_MESSAGE_SUCCESS_REVIEW_BULK_DONE', { updated: result.data.updated, skipped: result.data.skipped })
        })
        dispatch('fetchUsageReview')
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}
